<script setup lang="ts">
import * as yup from 'yup'
import { modelMapper, dataToError, type ModelData } from '~~/models/index'

useTitle('users.sign_in', { t: true })

definePageMeta({
  middleware: 'check-no-auth',
})

const { t, setLocale } = useI18n()

const route = useRoute()
const redirectCookie = useCookie('redirect')
const userState = useUserState()
const apiClient = useAPIClient()
const { redirectToRegion } = useRegion()

let redirect: string
if (route.query.redirect) {
  redirect = route.query.redirect as string
} else if (redirectCookie.value) {
  redirect = redirectCookie.value
}


const signUpSchema = useSchema(yup.object({
  login: yup.string().required()
    .meta({ label: t('users.login') }),
  password: yup.string().required()
    .meta({ label: t('users.password') }),
}))

const {
  handleSubmit, setFieldError, isSubmitting,
} = useForm({ validationSchema: signUpSchema })


const onSubmit = handleSubmit(async (values) => {
  const params = {
    login: values.login,
    password: values.password,
  }

  try {
    const data = await apiClient.fetch<ModelData>(
      'users/sign_in', { method: 'POST', body: params }
    )

    const tokenPair = modelMapper.TokenPair(data)
    userState.value.tokenPair = tokenPair

    if (redirect) return navigateTo(redirect)

    const { user } = await apiClient.user()
    if (user.value?.settings.locale) {
      setLocale(user.value.settings.locale)
    }

    return navigateTo('/users')
  } catch (err: any) {
    const apiErr = dataToError(err?.data as ModelData)
    if (apiErr.code == 'user_region_incorrect') {
      const redirectRegion = apiErr.data?.region
      return redirectToRegion({ region: redirectRegion, path: route.path })
    }

    apiClient.setFormErrors(setFieldError, 'users', err)
  }
})
</script>

<template>
  <SessionTabs>
    <div class="sign-in page">
      <div class="sign-in-form">
        <FlashMessage />
        <FormBase>
          <form @submit="onSubmit" class="form">
            <FormInput name="login" :label="t('users.login')" />
            <FormInput
              name="password"
              :label="t('users.password')"
              type="password"
            />
            <Captcha
              :show="apiClient.showCaptcha.value"
              @verified="p => apiClient.captchaPayload.value = p"
            />
            <button
              type="submit"
              class="button"
              :disabled="isSubmitting"
            >
              {{ t('users.sign_in') }}
            </button>
          </form>
        </FormBase>
      </div>

      <div class="links">
        <NuxtLinkLocale href="/users/reset">
          {{ t('users.forgot_details') }}
        </NuxtLinkLocale>
      </div>
    </div>
  </SessionTabs>
</template>

<style lang="scss" scoped>
.sign-in-form {
  width: 24rem;
  margin: 2rem auto 0 auto;

  :deep(.formkit-form) {
    input[type=text], input[type=password] { width: 100%; }
  }  
}

.links {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
}
</style>
